import React, {FC, useCallback} from 'react';

import {supportsPopout} from '../../../../../../libs/shared/helpers/src/environment/platformHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {EventHandler, Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {PermissionResult} from '../../../../../../src/helpers/permissionHelpers';
import {
  buildCopyLinkDataTestId,
  generateMessagePermalink,
} from '../../../../../../srcCommon/helpers/premalinksHelpers';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useDestroyMessageMessageSendingPermission} from '../../../../../../srcCommon/permissions/useMessageSendingPermission';
import {
  useComposeMessageOfflinePermission,
  useConversationSeenActionOfflinePermission,
} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {useClipboard} from '../../../../../helpers/hooks/clipboardHookHelpers';
import {buildMessagePublicId} from '../../../../../helpers/models/messageHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {LegacyMessageMoreDropdownEntryPointModel} from '../../../../../models/api/legacyEntryPointModel';
import {
  LoadedMessageModel,
  MessageStatusesEnum,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
} from '../../../../../models/api/messageModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {ReplyLocationsEnum} from '../../../../../services/tracking';
import {getCompanyPreferences} from '../../../../../store/data/company/companySelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../../store/panels/panelStateBase';
import {DropdownSeparator} from '../../../../common/dropdowns/dropdownSeparator';
import {PermissionSimpleDropdownItem} from '../../../../common/dropdowns/permissionSimpleDropdownItem';
import {PermalinkPermissionTooltip} from '../../conversation/actions/more/permalinkPermissionTooltip';

/*
 * Props.
 */

export type ForwardHandler = (location: ReplyLocationsEnum, shouldCreateConversation?: boolean) => void;
export type CloneHandler = (location: ReplyLocationsEnum, shouldCreateConversation: boolean) => void;

interface MessageViewerMoreDropdownProps extends PopoverTransferProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | ContactDetailsPanelId | AccountDetailsPanelId;
  namespaces: string[];
  host: string;
  cellsContext: CellsContext;
  message: LoadedMessageModel;
  entryPoints: ReadonlyArray<LegacyMessageMoreDropdownEntryPointModel>;
  canSplit: boolean;
  showReplyAction: boolean;
  isInTeammateContext: boolean;
  hasNewVisitorUrlExperience?: boolean;
  onReplyClick: HandlerOf<ReplyLocationsEnum>;
  onReplyAllClick: HandlerOf<ReplyLocationsEnum>;
  onForwardClick: ForwardHandler;
  onCloneClick: CloneHandler;
  onPrintClick: Handler;
  onSaveEmailClick: Handler;
  onViewSourceClick: Handler;
  onPopoutClick: Handler;
  onSplitClick: Handler;
  onMarkUnreadClick: Handler;
  onDetroyClick: Handler;
  onCreateRuleClick: EventHandler;
  onEntryPointSelect: HandlerOf<LegacyMessageMoreDropdownEntryPointModel>;
  openInBrowser: HandlerOf<string>;
}

/*
 * Component.
 */

export const MessageViewerMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  switch (props.message.type) {
    case MessageTypesEnum.EMAIL:
      return <EmailMoreDropdown {...props} />;

    case MessageTypesEnum.TWEET:
      return <TweetMoreDropdown {...props} />;

    case MessageTypesEnum.TWEET_DM:
      return <TweetDmMoreDropdown {...props} />;

    case MessageTypesEnum.SMS:
      return <SmsMoreDropdown {...props} />;

    case MessageTypesEnum.FACEBOOK:
      return <FacebookMoreDropdown {...props} />;

    case MessageTypesEnum.INTERCOM:
      return <IntercomMoreDropdown {...props} />;

    case MessageTypesEnum.FRONT_CHAT:
    case MessageTypesEnum.LAYER:
      return <LayerMoreDropdown {...props} />;

    case MessageTypesEnum.CUSTOM:
      return <CustomMoreDropdown {...props} />;

    default:
      return <OtherMoreDropdown {...props} />;
  }
};

const EmailMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const canPopout =
    [MessageStatusesEnum.SENT, MessageStatusesEnum.IMPORTED].includes(props.message.status) &&
    RViewerPanelId.is(props.panelId) &&
    supportsPopout();

  const messageComposeOfflinePermission = useComposeMessageOfflinePermission();
  const isEMLFileViewer = props.message.visualStatus === MessageVisualStatusesEnum.EML_FILE;

  return (
    <SimpleDropdown {...props} placement="bottom-end">
      <ReplyDropdownItems {...props} />
      <PermissionCheck offlinePermission={messageComposeOfflinePermission}>
        {(permission) => (
          <>
            <ForwardDropdownItems
              canSendEmails={permission.isAllowed}
              onForwardClick={props.onForwardClick}
              isEMLFileViewer={isEMLFileViewer}
            />
            {!isEMLFileViewer && (
              <CloneDropdownItems canSendEmails={permission.isAllowed} onCloneClick={props.onCloneClick} />
            )}
          </>
        )}
      </PermissionCheck>
      {!isEMLFileViewer && <MarkUnreadDropdownItem {...props} />}
      {props.isInTeammateContext && (
        <PermissionSimpleDropdownItem onSelect={props.onCreateRuleClick}>
          <Trans id="message-actions-createrule">Create quick rule</Trans>
        </PermissionSimpleDropdownItem>
      )}
      {!isEMLFileViewer && (
        <>
          <SplitDropdownItem {...props} />
          <DestroyDropdownItem {...props} />
        </>
      )}
      <DropdownSeparator />
      <SimpleDropdownItem onSelect={props.onPrintClick}>
        <Trans id="message-actions-print">Print</Trans>
      </SimpleDropdownItem>
      {!isEMLFileViewer && (
        <>
          {props.message.type === MessageTypesEnum.EMAIL && (
            <SimpleDropdownItem onSelect={props.onSaveEmailClick}>
              <Trans id="message-actions-save">Save</Trans>
            </SimpleDropdownItem>
          )}
          <DropdownSeparator />
          <EntryPointDropdownItems {...props} />
          <CopyDropdownItems {...props} />
          <SimpleDropdownItem onSelect={props.onViewSourceClick}>
            <Trans id="message-actions-viewsource">View source</Trans>
          </SimpleDropdownItem>
          {canPopout && (
            <SimpleDropdownItem onSelect={props.onPopoutClick}>
              <Trans id="message-actions-popout">View in external window</Trans>
            </SimpleDropdownItem>
          )}
        </>
      )}
    </SimpleDropdown>
  );
};

const TweetMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, openInBrowser} = props;
  const twitterExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.twitterExtra;

  return (
    <SimpleDropdown {...props}>
      <ReplyDropdownItems {...props} />
      <MarkUnreadDropdownItem {...props} />
      {twitterExtra && (
        <SimpleDropdownItem onSelect={() => openInBrowser(twitterExtra.tweetUrl)}>
          <Trans id="message-actions-viewontwitter">View on Twitter</Trans>
        </SimpleDropdownItem>
      )}
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const TweetDmMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, openInBrowser} = props;
  const twitterDmExtra =
    message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.twitterDmExtra;

  return (
    <SimpleDropdown {...props}>
      <ReplyDropdownItem {...props} />
      <MarkUnreadDropdownItem {...props} />
      {twitterDmExtra && (
        <SimpleDropdownItem onSelect={() => openInBrowser(twitterDmExtra.dmUrl)}>
          <Trans id="message-actions-viewontwitter">View on Twitter</Trans>
        </SimpleDropdownItem>
      )}
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const SmsMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    <ReplyDropdownItem {...props} />
    <MarkUnreadDropdownItem {...props} />
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const FacebookMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    <ReplyDropdownItem {...props} />
    <MarkUnreadDropdownItem {...props} />
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const CustomMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    <ReplyDropdownItem {...props} />
    <MarkUnreadDropdownItem {...props} />
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const IntercomMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, openInBrowser} = props;
  const intercomExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.intercomExtra;

  return (
    <SimpleDropdown {...props}>
      <ReplyDropdownItem {...props} />
      <MarkUnreadDropdownItem {...props} />
      {intercomExtra && (
        <SimpleDropdownItem onSelect={() => openInBrowser(intercomExtra.intercomUrl)}>
          <Trans id="message-actions-viewonintercom">View on Intercom</Trans>
        </SimpleDropdownItem>
      )}
      <SplitDropdownItem {...props} />
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const LayerMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {copy} = useClipboard();
  const {message, openInBrowser, hasNewVisitorUrlExperience} = props;
  const layerExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.layerExtra;

  return (
    <SimpleDropdown {...props}>
      {layerExtra && !hasNewVisitorUrlExperience && (
        <>
          <SimpleDropdownItem onSelect={() => openInBrowser(layerExtra.layerPageUrl)}>
            <Trans id="message-actions-viewonlayer">Open visitor URL</Trans>
          </SimpleDropdownItem>
          <SimpleDropdownItem onSelect={() => copy(layerExtra.layerPageUrl)}>
            <Trans id="message-actions-copylayer">Copy visitor URL</Trans>
          </SimpleDropdownItem>
        </>
      )}
      <MarkUnreadDropdownItem {...props} />
      <SplitDropdownItem {...props} />
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const OtherMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    <MarkUnreadDropdownItem {...props} />
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

/*
 * Various actions.
 */

const ReplyDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {onReplyClick, showReplyAction} = props;
  const offlinePermission = useComposeMessageOfflinePermission();

  if (!showReplyAction) {
    return null;
  }

  return (
    <PermissionCheck offlinePermission={offlinePermission}>
      {(permission) => (
        <SimpleDropdownItem
          onSelect={() => onReplyClick(ReplyLocationsEnum.THREE_DOTS)}
          isDisabled={!permission.isAllowed}
        >
          <Trans id="message-actions-reply">Reply</Trans>
        </SimpleDropdownItem>
      )}
    </PermissionCheck>
  );
};

const ReplyDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {onReplyClick, onReplyAllClick, showReplyAction} = props;
  const offlinePermission = useComposeMessageOfflinePermission();

  return (
    <PermissionCheck offlinePermission={offlinePermission}>
      {(permission) => (
        <>
          <SimpleDropdownItem
            onSelect={() => onReplyClick(ReplyLocationsEnum.THREE_DOTS)}
            isDisabled={!permission.isAllowed || !showReplyAction}
          >
            <Trans id="message-actions-reply">Reply</Trans>
          </SimpleDropdownItem>
          <SimpleDropdownItem
            onSelect={() => onReplyAllClick(ReplyLocationsEnum.THREE_DOTS)}
            isDisabled={!permission.isAllowed || !showReplyAction}
          >
            <Trans id="message-actions-replyall">Reply all</Trans>
          </SimpleDropdownItem>
        </>
      )}
    </PermissionCheck>
  );
};

interface ForwardDropdownItemsProps {
  canSendEmails: boolean;
  onForwardClick: ForwardHandler;
  isEMLFileViewer: boolean;
}

const ForwardDropdownItems: FC<ForwardDropdownItemsProps> = (props) => {
  /* You can forward a message even if you don't have the permission to reply. */
  const {canSendEmails, onForwardClick} = props;
  const offlinePermission = useComposeMessageOfflinePermission();

  return (
    <>
      <PermissionSimpleDropdownItem
        offlinePermission={offlinePermission}
        onSelect={() => onForwardClick(ReplyLocationsEnum.THREE_DOTS)}
        isDisabled={!canSendEmails}
      >
        <Trans id="forward-existing-conversation-title">Forward</Trans>
      </PermissionSimpleDropdownItem>
      {!props.isEMLFileViewer && (
        <PermissionSimpleDropdownItem
          offlinePermission={offlinePermission}
          onSelect={() => onForwardClick(ReplyLocationsEnum.THREE_DOTS, true)}
          isDisabled={!canSendEmails}
        >
          <Trans id="forward-new-conversation-title">Forward as a new conversation</Trans>
        </PermissionSimpleDropdownItem>
      )}
    </>
  );
};

interface CloneDropdownItemsProps {
  canSendEmails: boolean;
  onCloneClick: CloneHandler;
}

const CloneDropdownItems: FC<CloneDropdownItemsProps> = (props) => {
  /* You can resend a message even if you don't have the permission to reply. */
  const {canSendEmails, onCloneClick} = props;
  const offlinePermission = useComposeMessageOfflinePermission();

  return (
    <>
      <PermissionSimpleDropdownItem
        offlinePermission={offlinePermission}
        onSelect={() => onCloneClick(ReplyLocationsEnum.THREE_DOTS, false)}
        isDisabled={!canSendEmails}
      >
        <Trans id="clone-existing-conversation-title">Resend</Trans>
      </PermissionSimpleDropdownItem>
      <PermissionSimpleDropdownItem
        offlinePermission={offlinePermission}
        onSelect={() => onCloneClick(ReplyLocationsEnum.THREE_DOTS, true)}
        isDisabled={!canSendEmails}
      >
        <Trans id="clone-new-conversation-title">Resend as a new conversation</Trans>
      </PermissionSimpleDropdownItem>
    </>
  );
};

const SplitDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, canSplit, onSplitClick} = props;
  if (message.type === MessageTypesEnum.EMAIL) {
    return (
      <PermissionSimpleDropdownItem onSelect={onSplitClick} isDisabled={!canSplit}>
        <Trans id="message-actions-splitconversation-email">Move to a new conversation</Trans>
      </PermissionSimpleDropdownItem>
    );
  }

  return (
    <PermissionSimpleDropdownItem onSelect={onSplitClick} isDisabled={!canSplit}>
      <Trans id="message-actions-splitconversation-other">Split conversation</Trans>
    </PermissionSimpleDropdownItem>
  );
};

const MarkUnreadDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {onMarkUnreadClick} = props;
  const offlinePermission = useConversationSeenActionOfflinePermission();

  return (
    <PermissionSimpleDropdownItem onSelect={onMarkUnreadClick} offlinePermission={offlinePermission}>
      <Trans id="message-actions-markmessageunread">Mark as unread</Trans>
    </PermissionSimpleDropdownItem>
  );
};

const DestroyDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {namespaces, onDetroyClick, message} = props;
  const messageSendingPermission = useDestroyMessageMessageSendingPermission(message);
  const makeRenderTooltip = (permission: PermissionResult) =>
    !permission.isAllowed ? () => <PermissionDenied reason={permission.reason} /> : undefined;

  return (
    <PermissionCheck
      namespaces={namespaces}
      permission={PermissionsEnum.CONVERSATION_DESTROY}
      messageSendingPermission={messageSendingPermission}
    >
      {(permission) => (
        <SimpleDropdownItem
          renderTooltip={makeRenderTooltip(permission)}
          onSelect={onDetroyClick}
          isDisabled={!permission.isAllowed}
        >
          <Trans id="message-actions-deletepermanently">Delete permanently</Trans>
        </SimpleDropdownItem>
      )}
    </PermissionCheck>
  );
};

const EntryPointDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {entryPoints, onEntryPointSelect} = props;
  return (
    <>
      {entryPoints.map((entryPoint) => (
        <SimpleDropdownItem key={entryPoint.uid} onSelect={() => onEntryPointSelect(entryPoint)}>
          {entryPoint.name}
        </SimpleDropdownItem>
      ))}
    </>
  );
};

const CopyDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {copy} = useClipboard();
  const {host, message} = props;
  const preferences = useAppSelector(getCompanyPreferences);
  const twitterExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.twitterExtra;

  /*
   * Event handlers.
   */

  const onCopyMessageIdClick = useCallback(() => {
    copy(buildMessagePublicId(message));
  }, [copy, message]);

  const onCopyMessageLinkClick = useCallback(async () => {
    copy(generateMessagePermalink(message, host, preferences));
  }, [copy, host, message, preferences]);

  const onCopyTweetLinkClick = useCallback(() => {
    if (twitterExtra) {
      copy(twitterExtra.tweetUrl);
    }
  }, [copy, twitterExtra]);

  /*
   * Render.
   */

  return (
    <>
      <SimpleDropdownItem onSelect={onCopyMessageIdClick}>
        <Trans id="message-actions-copyid">Copy message ID</Trans>
      </SimpleDropdownItem>
      <DropdownItem
        onSelect={onCopyMessageLinkClick}
        interactionId={buildCopyLinkDataTestId('message', preferences)}
      >
        <DropdownItemContent renderTooltip={() => <PermalinkPermissionTooltip preferences={preferences} />}>
          <Trans id="message-actions-copylink">Copy message link</Trans>
        </DropdownItemContent>
      </DropdownItem>
      {twitterExtra && (
        <SimpleDropdownItem onSelect={onCopyTweetLinkClick}>
          <Trans id="message-actions-copytweetlink">Copy link to Tweet</Trans>
        </SimpleDropdownItem>
      )}
    </>
  );
};
